import { Sheet, SheetDescription, SheetHeader, SheetTitle } from '@reflex/ui/components/ui/sheet';
import { Button } from '@reflex/ui/components/ui/button';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { ResizableSheetContent } from '@/components/sidebar/ResizableSheetContent';
import { LogViewer } from '@reflex/ui/components/content/log-viewer';
import { useServiceLog } from '@/hooks/agents/useServiceStatuses';

/** The server the log sheet is showing: its on-box service id + display name. */
export interface ServiceLogTarget {
  serviceId: string;
  name: string;
}

/**
 * Slide-over showing the on-box log file of one sandbox service (a daemon the
 * agent registered, or a launch step). Controlled by the parent: pass a
 * {@link ServiceLogTarget} to open, null to close — the server list rows are
 * the triggers, so the sheet itself owns no open state. Mirrors the devbox
 * platform-logs sheet (ViewLogsButton) in layout and viewer.
 */
export function ServiceLogSheet({
  agentId,
  target,
  onOpenChange,
}: {
  agentId: string;
  target: ServiceLogTarget | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, error, refetch, isRefetching } = useServiceLog(
    agentId,
    target?.serviceId,
  );

  return (
    <Sheet open={target !== null} onOpenChange={onOpenChange}>
      <ResizableSheetContent
        defaultWidth={600}
        minWidth={400}
        maxWidth={1200}
        storageKey="service-log-panel-width"
        className="flex flex-col"
        data-testid="service-log-sheet"
      >
        <SheetHeader className="px-6 pt-6 pb-2 shrink-0">
          <SheetTitle className="text-base">{target ? `${target.name} logs` : 'Logs'}</SheetTitle>
          <SheetDescription className="text-xs">
            Output of the {target?.name ?? 'service'} process on the agent&apos;s sandbox
            {target ? ` (~/.reflex/logs/${target.serviceId}.log)` : ''}.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 min-h-0 px-6 pb-6 flex flex-col">
          {isLoading ? (
            <PageDescription className="mt-0 py-4">Loading&hellip;</PageDescription>
          ) : error ? (
            <PageDescription className="mt-0 py-4 text-sm text-destructive">
              {toErrorMessage(error)}
            </PageDescription>
          ) : data !== undefined ? (
            <div className="flex-1 min-h-0">
              <LogViewer content={data} path={`${target?.serviceId ?? 'service'}.log`} />
            </div>
          ) : null}
          {data !== undefined && !isLoading && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3 shrink-0"
              disabled={isRefetching}
              onClick={() => void refetch()}
            >
              Refresh
            </Button>
          )}
        </div>
      </ResizableSheetContent>
    </Sheet>
  );
}
